import { formatDistance } from 'date-fns'
import { enUS } from 'date-fns/locale'
import type { Locale } from 'date-fns'

type DistanceToken = Parameters<Locale['formatDistance']>[0]

/**
 * Short unit per date-fns distance token. The long form ("about 2 years ago")
 * and the short form ("2y") are produced by the same `formatDistance` call, with
 * only the locale's wording swapped, so they can never disagree about the count
 * or where a unit boundary falls (an independent calendar-difference version
 * said "1mo" for dates the long form called "about 1 month" and "2mo" for 45 days).
 */
const SHORT_UNITS: Record<DistanceToken, string> = {
    // Under a minute: date-fns reports "less than a minute", which reads as "now".
    lessThanXSeconds: 'now',
    xSeconds: 'now',
    halfAMinute: 'now',
    lessThanXMinutes: 'now',
    xMinutes: '{{count}}m',
    aboutXHours: '{{count}}h',
    xHours: '{{count}}h',
    xDays: '{{count}}d',
    aboutXWeeks: '{{count}}w',
    xWeeks: '{{count}}w',
    aboutXMonths: '{{count}}mo',
    xMonths: '{{count}}mo',
    aboutXYears: '{{count}}y',
    xYears: '{{count}}y',
    overXYears: '{{count}}y',
    almostXYears: '{{count}}y'
}

// Spread from the locale the long form uses (the date-fns default, enUS) so
// only the distance wording differs.
const shortLocale: Locale = {
    ...enUS,
    formatDistance: (token, count) => SHORT_UNITS[token].replace('{{count}}', String(count))
}

/**
 * Compact age for narrow cards ("2y", "6mo", "3d", "5h", "12m", "now").
 *
 * Takes `now` as a parameter so boundaries are testable without fake timers;
 * `formatDistanceToNow(date)` is `formatDistance(date, Date.now())`, so the
 * default matches the long form exactly.
 */
export function formatShortRelativeDate(date: Date, now: Date = new Date()): string {
    return formatDistance(date, now, { locale: shortLocale })
}
